import { useCallback, useMemo, useRef, useState } from 'react';

export type ValidatedInput<T> = {
  value: T;
  error: string;

  isFocused: boolean;
  isDirty: boolean;
};

/**
 * TODO: Add more extensive documentation. Check the examples in the repo for now.
 * TODO: Add tests for this hook.
 *
 * @param initialValue - The initial value of the input.
 * @param validation - A function that validates new values. If the return is an empty string, we assume the value is valid.
 * @returns
 */
export function useValidatedState<T>(
  initialValue: T,
  validation: (value: T) => string,
) {
  const validationRef = useRef(validation);

  const [value, setValue] = useState<T>(initialValue);
  const [isDirty, setIsDirty] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const error = useMemo(() => validationRef.current(value), [value]);

  const validate = useCallback(() => {
    setIsDirty(true);

    return !error;
  }, [error]);

  const setValueAndValidate = useCallback(
    (newValue: T) => {
      setValue(newValue);
      setIsDirty(true);
    },
    [setValue],
  );

  const returnValue = useMemo(() => {
    return {
      value,
      error,
      showError: !isFocused && isDirty && !!error,

      validate,
      set: setValueAndValidate,
      setFocus: () => setIsFocused(true),
      setBlur: () => setIsFocused(false),
    };
  }, [value, error, isFocused, isDirty, setValueAndValidate, validate]);

  return returnValue;
}
